import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import API from '../../../../services/API'
import {
  GlobalContext,
  TenantSettingsContext,
  UserContext,
} from '../../../../App'
import SelectedApplication from '../../ShiftApplications/SelectedApplication'
import RosterControlPanel from './RosterControlPanel'
import {
  ArchiveRosterDialog,
  NewRosterDialog,
  PublishRosterDialog,
  RosterPublishedDialog,
} from './RosterDialogs'
import RosterGrid from './RosterGrid'
import RosterMonthCalendar from './RosterMonthCalendar'
import RosterStatusStrip from './RosterStatusStrip'
import RosterYearOverview from './RosterYearOverview'
import ShiftDrawer from './ShiftDrawer'
import {
  buildAvailableStaffByDay,
  createShiftDraft,
  getCoverageItems,
  getEmployeeName,
  getEntityId,
  getPtsOrganisationLabel,
  getShiftSource,
  getShiftSourceLabel,
  isAvailableRecord,
  PTS_SHARED_ORGANISATION_LABEL,
  isDraftRosterShift,
  isOnCallShift,
  rowHasAvailableWindow,
  toShiftDraft,
} from './globalRosterUtils'
import { getShiftIdentityKey } from '../../../../helpers/shiftIdentity'

import Drawer from '@mui/material/Drawer'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

const getSelectedEmployeeID = (selection) => {
  if (!selection || selection.subcontractor) return null
  return selection.employee?._id ?? selection._id ?? null
}

const getSelectedSubcontractorID = (selection) =>
  selection?.subcontractor?._id ?? null

const VIEW_RANGES = ['week', 'month', 'year']
const BUILT_IN_DEFAULT_VIEW = 'year'

const GlobalRosterCalendar = () => {
  const { orgConfig } = useContext(TenantSettingsContext)
  const { baseLocationOptions, setToast } = useContext(GlobalContext)
  const { user, setUser } = useContext(UserContext)
  // Per-user landing view, saved to uiPreferences from the control panel
  const savedDefaultView = VIEW_RANGES.includes(
    user?.uiPreferences?.globalRosterDefaultView,
  )
    ? user.uiPreferences.globalRosterDefaultView
    : null
  const defaultView = savedDefaultView ?? BUILT_IN_DEFAULT_VIEW
  const [savingDefaultView, setSavingDefaultView] = useState(false)
  const orgSettings = orgConfig?.orgSettings ?? {}
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const requestedDate = searchParams.get('date')
  const requestedShiftID = searchParams.get('shift')

  const [shifts, setShifts] = useState([])
  // Staff availability (My Availability records) for the visible week -
  // drawn as bands on the staff rows and behind the "available only" toggle
  const [availability, setAvailability] = useState([])
  const [showAvailableOnly, setShowAvailableOnly] = useState(false)
  // PTS duties in the visible range with no crew shifts at all
  const [uncrewedDuties, setUncrewedDuties] = useState([])
  const [rosters, setRosters] = useState([])
  const [selectedRoster, setSelectedRoster] = useState(null)
  const [loading, setLoading] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [applications, setApplications] = useState([])
  const [shiftSwapRequests, setShiftSwapRequests] = useState([])
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [selectedShift, setSelectedShift] = useState(null)
  const [shiftForm, setShiftForm] = useState(null)
  const [shiftSaving, setShiftSaving] = useState(false)
  const [releasingShift, setReleasingShift] = useState(false)
  const [assignmentIssues, setAssignmentIssues] = useState([])
  const [checkingAssignment, setCheckingAssignment] = useState(false)
  const [rosterDialogOpen, setRosterDialogOpen] = useState(false)
  const [rosterSaving, setRosterSaving] = useState(false)
  const [archiveRosterOpen, setArchiveRosterOpen] = useState(false)
  const [archiveRosterError, setArchiveRosterError] = useState('')
  const [publishRosterOpen, setPublishRosterOpen] = useState(false)
  const [publishSummary, setPublishSummary] = useState(null)
  const [publishingRoster, setPublishingRoster] = useState(false)
  const [publishedRosterInfo, setPublishedRosterInfo] = useState(null)
  const lastAssignmentCheckKey = useRef('')
  const openedRequestedShiftKey = useRef('')

  const [displayMode, setDisplayMode] = useState('staff')
  const [filter, setFilter] = useState('all')
  // 'week' | 'month' | 'year'. The visible range is derived from an anchor
  // date snapped to the start of the chosen unit, so switching views keeps
  // the user in the same part of the calendar.
  // A ?date= deep link always lands on that week; otherwise the user's
  // saved default (built-in: year)
  const [viewRange, setViewRange] = useState(() =>
    moment(requestedDate, 'YYYY-MM-DD', true).isValid() ? 'week' : defaultView,
  )
  const [anchorDate, setAnchorDate] = useState(() => {
    const parsedDate = moment(requestedDate, 'YYYY-MM-DD', true)
    return parsedDate.isValid() ? parsedDate : moment()
  })
  // The year view opens on the next 12 months (current month first) and
  // switches to calendar years as soon as the user pages with the arrows
  const [yearMode, setYearMode] = useState('rolling')
  const isRollingYear = viewRange === 'year' && yearMode === 'rolling'
  const rollingWindowStart = () => moment().startOf('month')

  const saveDefaultView = () => {
    setSavingDefaultView(true)
    API.post('/account/uipreferences', { globalRosterDefaultView: viewRange })
      .then((res) => {
        if (res.status === 200) {
          setUser((prev) => ({ ...prev, uiPreferences: res.data }))
          setToast({
            message: `${viewRange[0].toUpperCase()}${viewRange.slice(1)} view is now your default`,
          })
        } else {
          setToast({
            severity: 'error',
            message: 'Unable to save your default view',
          })
        }
      })
      .finally(() => setSavingDefaultView(false))
  }
  const rosterFormDefault = {
    title: '',
    description: '',
    baseLocation: null,
    isOnCall: false,
  }
  const [rosterForm, setRosterForm] = useState(rosterFormDefault)

  const openRosterDialog = () => {
    setRosterDialogOpen(true)
  }

  const days = useMemo(() => {
    const rangeStart = isRollingYear
      ? rollingWindowStart()
      : moment(anchorDate).startOf(viewRange)
    const length =
      viewRange === 'week'
        ? 7
        : viewRange === 'month'
          ? rangeStart.daysInMonth()
          : isRollingYear
            ? rangeStart.clone().add(12, 'months').diff(rangeStart, 'days')
            : rangeStart.isLeapYear()
              ? 366
              : 365

    return Array.from({ length }, (_, i) => rangeStart.clone().add(i, 'days'))
  }, [anchorDate, viewRange, isRollingYear])

  const startDate = days[0]
  const endDate = days[days.length - 1]
  const rangeLabel =
    viewRange === 'week'
      ? `${startDate.format('D MMM')} - ${endDate.format('D MMM')}`
      : viewRange === 'month'
        ? startDate.format('MMMM YYYY')
        : isRollingYear
          ? `Next 12 months · ${startDate.format('MMM YYYY')} – ${endDate.format('MMM YYYY')}`
          : startDate.format('YYYY')
  // Phones: "September 2026" doesn't fit between the arrows
  const rangeLabelShort =
    viewRange === 'month'
      ? startDate.format('MMM YYYY')
      : isRollingYear
        ? `${startDate.format('MMM YY')} – ${endDate.format('MMM YY')}`
        : rangeLabel

  useEffect(() => {
    const parsedDate = moment(requestedDate, 'YYYY-MM-DD', true)
    if (!parsedDate.isValid()) return

    setViewRange('week')
    setAnchorDate((prev) =>
      moment(prev).isSame(parsedDate, 'day') ? prev : parsedDate,
    )
  }, [requestedDate])

  // Entering the year view: the rolling window when the anchor falls inside
  // the next 12 months (the usual case), otherwise the anchor's calendar year
  useEffect(() => {
    if (viewRange !== 'year') return
    const windowStart = rollingWindowStart()
    const windowEnd = windowStart.clone().add(12, 'months')
    setYearMode(
      moment(anchorDate).isBetween(windowStart, windowEnd, undefined, '[)')
        ? 'rolling'
        : 'calendar',
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewRange])

  const showNext12Months = () => {
    setAnchorDate(moment())
    setYearMode('rolling')
  }

  // Month/year day clicks drill down to the week containing that day
  const openWeekOf = (day) => {
    setViewRange('week')
    setAnchorDate(moment(day))
  }

  const loadRosters = () => {
    API.get('/rosters', {
      params: {
        status: 'Draft',
      },
    }).then((res) => {
      if (res.status === 200) {
        setRosters(res.data)
        setSelectedRoster((prev) => {
          if (prev && res.data.some((roster) => roster._id === prev._id)) {
            return prev
          }
          return res.data?.[0] ?? null
        })
      }
    })
  }

  const loadShifts = () => {
    setLoading(true)
    API.post('/get-global-roster-shifts', {
      start: startDate.toISOString(),
      end: endDate.clone().endOf('day').toISOString(),
      // The year view only colours days by status, so it asks for the
      // trimmed summary documents rather than a year of full shift cards
      summary: viewRange === 'year',
    })
      .then((res) => res.status === 200 && setShifts(res.data))
      .finally(() => setLoading(false))
  }

  const loadApplications = () => {
    API.get('/shift-applications').then((res) => {
      if (res.status === 200) setApplications(res.data ?? [])
    })
  }

  const loadShiftSwapRequests = () => {
    API.get('/shift-swap-requests').then((res) => {
      if (res.status === 200) setShiftSwapRequests(res.data ?? [])
    })
  }

  // Staff availability for the visible range: bands on the week grid's
  // staff rows, per-day counts everywhere else, green rings on the year view
  const loadAvailability = () => {
    API.get('/staff-availability', {
      params: {
        start: startDate.toISOString(),
        end: endDate.clone().endOf('day').toISOString(),
        status: 'Active',
      },
    })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setAvailability(res.data)
        }
      })
      .catch(() => {})
  }

  // Only the week grid has somewhere to draw them
  const loadUncrewedDuties = () => {
    if (viewRange !== 'week') {
      setUncrewedDuties([])
      return
    }
    API.get('/global-roster/uncrewed-pts-duties', {
      params: {
        start: startDate.toISOString(),
        end: endDate.clone().endOf('day').toISOString(),
      },
    })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setUncrewedDuties(res.data)
        }
      })
      .catch(() => {})
  }

  useEffect(() => {
    loadRosters()
    loadShifts()
    loadApplications()
    loadShiftSwapRequests()
    loadAvailability()
    loadUncrewedDuties()
  }, [startDate.valueOf(), endDate.valueOf()])

  // Filters that can't contain a crew-less PTS duty hide the row
  const visibleUncrewedDuties = ['all', 'vacant', 'pts'].includes(filter)
    ? uncrewedDuties
    : []

  // The year view only offers Coverage (staff) and Source - snap back if a
  // row-based mode was selected in another view
  useEffect(() => {
    if (viewRange === 'year' && !['staff', 'source'].includes(displayMode)) {
      setDisplayMode('staff')
    }
  }, [viewRange, displayMode])

  const shiftFormRoster =
    rosters.find((r) => r._id === shiftForm?.roster) ?? null
  const shiftFormIsOnCall = shiftFormRoster
    ? !!shiftFormRoster.isOnCall
    : !!(
        selectedShift?.roster?._id === shiftForm?.roster &&
        isOnCallShift(selectedShift)
      )

  useEffect(() => {
    const selectedEmployeeID = getSelectedEmployeeID(shiftForm?.assignedTo)

    if (
      !drawerOpen ||
      !selectedEmployeeID ||
      !shiftForm?.start ||
      !shiftForm?.end ||
      (!shiftForm?.clinicalRole && !shiftFormIsOnCall)
    ) {
      setAssignmentIssues([])
      return undefined
    }

    const timer = setTimeout(() => {
      const nonRoleCheckKey = [
        selectedEmployeeID,
        shiftForm.start.toISOString(),
        shiftForm.end.toISOString(),
        shiftForm.existingShift ?? '',
      ].join('|')
      const showProgress = lastAssignmentCheckKey.current !== nonRoleCheckKey
      lastAssignmentCheckKey.current = nonRoleCheckKey

      if (showProgress) {
        setCheckingAssignment(true)
      }

      API.post('/shift-assignment-issues', {
        employee: selectedEmployeeID,
        start: shiftForm.start.toISOString(),
        end: shiftForm.end.toISOString(),
        clinicalRole: shiftForm.clinicalRole,
        excludeShift: shiftForm.existingShift,
        isOnCall: shiftFormIsOnCall,
      })
        .then((res) => {
          if (res.status === 200) setAssignmentIssues(res.data)
        })
        .finally(() => {
          if (showProgress) {
            setCheckingAssignment(false)
          }
        })
    }, 300)

    return () => clearTimeout(timer)
  }, [
    drawerOpen,
    shiftForm?.assignedTo,
    shiftForm?.start,
    shiftForm?.end,
    shiftForm?.clinicalRole,
    shiftForm?.existingShift,
    shiftFormIsOnCall,
  ])

  const filteredShifts = shifts.filter((shift) => {
    if (isDraftRosterShift(shift)) {
      const belongsToSelectedRoster =
        !!selectedRoster?._id &&
        getEntityId(shift.roster) === selectedRoster._id

      if (!belongsToSelectedRoster) return false
    }

    if (filter === 'vacant')
      return (
        !shift.assignedTo &&
        !shift.assignedSubcontractor &&
        !shift.assignedToSubcontractor
      )
    if (filter === 'assigned')
      return (
        !!shift.assignedTo ||
        !!shift.assignedSubcontractor ||
        !!shift.assignedToSubcontractor
      )
    if (filter === 'roster') return !!shift.roster
    if (filter === 'oncall') return isOnCallShift(shift)
    if (filter === 'events') return !!shift.event || shift.shiftType === 'Event'
    if (filter === 'pts') return !!shift.ptsDuty || shift.shiftType === 'PTS'
    return true
  })
  const coverageItems = getCoverageItems(filteredShifts)

  const onCallNowNames = useMemo(() => {
    const now = moment()
    const names = shifts
      .filter(
        (shift) =>
          isOnCallShift(shift) &&
          !isDraftRosterShift(shift) &&
          shift.assignedTo &&
          now.isBetween(moment(shift.start), moment(shift.end), 'minute', '[]'),
      )
      .map((shift) => getEmployeeName(shift.assignedTo))
      .filter(Boolean)
    return [...new Set(names)]
  }, [shifts])

  const availabilityByEmployee = useMemo(() => {
    const index = new Map()
    availability.forEach((record) => {
      const employeeID = getEntityId(record.employee)
      if (!employeeID) return
      if (!index.has(employeeID)) {
        index.set(employeeID, { employee: record.employee, records: [] })
      }
      index.get(employeeID).records.push(record)
    })
    return index
  }, [availability])

  const availableStaffCount = useMemo(
    () =>
      [...availabilityByEmployee.values()].filter(({ records }) =>
        records.some(isAvailableRecord),
      ).length,
    [availabilityByEmployee],
  )

  // YYYY-MM-DD → { count, names } for the month footers, the week view's
  // "Available staff" row and the year view's day rings
  const availableStaffByDay = useMemo(
    () => buildAvailableStaffByDay(availability, startDate, endDate),
    [availability, startDate.valueOf(), endDate.valueOf()],
  )

  const groupedRows = useMemo(() => {
    const groups = {}

    filteredShifts.forEach((shift) => {
      let id = 'vacant'
      let label = 'Vacant Shifts'
      let subLabel = ''
      let staff = null

      if (displayMode === 'role') {
        id = shift.clinicalRole || 'Unspecified'
        label = id
      } else if (displayMode === 'source') {
        // One row per event; PTS split by the duty's dedicated organisation
        id = getShiftSourceLabel(shift)
        label = getShiftSource(shift)
        subLabel = getPtsOrganisationLabel(shift)
      } else if (displayMode === 'roster') {
        id = shift.roster?._id || getShiftSource(shift)
        label = shift.roster?.title || getShiftSource(shift)
        subLabel = [shift.roster?.status, isOnCallShift(shift) && 'On Call']
          .filter(Boolean)
          .join(' • ')
      } else if (shift.assignedTo) {
        id = shift.assignedTo._id
        label = `${shift.assignedTo?.userAccount?.firstName ?? ''} ${
          shift.assignedTo?.userAccount?.lastName ?? ''
        }`.trim()
        subLabel = shift.assignedTo?.clinicalRole ?? ''
        staff = shift.assignedTo
      } else if (shift.assignedSubcontractor || shift.assignedToSubcontractor) {
        id = 'subcontractor'
        label = 'Subcontractor'
        subLabel = 'Contractor assignments'
      }

      if (!groups[id]) {
        groups[id] = { id, label, subLabel, staff, shifts: [] }
      }

      groups[id].shifts.push(shift)
    })

    // Staff view: everyone who has told us when they can (or can't) work
    // gets a row even without a shift, so planners can find free people
    // and see leave alongside the rota
    if (displayMode === 'staff') {
      availabilityByEmployee.forEach(({ employee, records }, employeeID) => {
        if (!groups[employeeID]) {
          groups[employeeID] = {
            id: employeeID,
            label: getEmployeeName(employee),
            subLabel: employee?.clinicalRole ?? '',
            staff: employee,
            shifts: [],
          }
        }
        groups[employeeID].availability = records
      })
    }

    let rows = Object.values(groups)
    if (displayMode === 'staff' && showAvailableOnly) {
      rows = rows.filter(
        (row) => row.id === 'vacant' || rowHasAvailableWindow(row),
      )
    }

    return rows.sort((a, b) => {
      if (a.id === 'vacant') return -1
      if (b.id === 'vacant') return 1
      if (a.id === 'subcontractor') return 1
      if (b.id === 'subcontractor') return -1
      // Named PTS organisations first, the shared row after them
      const aShared = a.subLabel === PTS_SHARED_ORGANISATION_LABEL
      const bShared = b.subLabel === PTS_SHARED_ORGANISATION_LABEL
      return (
        a.label.localeCompare(b.label) ||
        Number(aShared) - Number(bShared) ||
        (a.subLabel ?? '').localeCompare(b.subLabel ?? '')
      )
    })
  }, [displayMode, filteredShifts, availabilityByEmployee, showAvailableOnly])

  // Paging out of the rolling window lands on a calendar year: back to this
  // year (it starts earlier), forward to next year
  const handlePrev = () => {
    if (isRollingYear) {
      setYearMode('calendar')
      setAnchorDate(moment().startOf('year'))
      return
    }
    setAnchorDate((prev) => moment(prev).startOf(viewRange).subtract(1, viewRange))
  }

  const handleNext = () => {
    if (isRollingYear) {
      setYearMode('calendar')
      setAnchorDate(moment().startOf('year').add(1, 'year'))
      return
    }
    setAnchorDate((prev) => moment(prev).startOf(viewRange).add(1, viewRange))
  }

  const openNewShift = (day, assignedTo = null) => {
    if (!selectedRoster) {
      setRosterDialogOpen(true)
      return
    }

    setAssignmentIssues([])
    setSelectedShift(null)
    setShiftForm(
      createShiftDraft({
        day,
        roster: selectedRoster,
        orgSettings,
        assignedTo,
      }),
    )
    setDrawerOpen(true)
  }

  const openExistingShift = (shift) => {
    setAssignmentIssues([])
    setSelectedShift(shift)
    setShiftForm(toShiftDraft(shift))
    setDrawerOpen(true)
  }

  const copyShiftToSelectedRoster = () => {
    if (!selectedShift || !selectedRoster?._id) return

    setAssignmentIssues([])
    setSelectedShift(null)
    setShiftForm({
      ...toShiftDraft(selectedShift),
      existingShift: undefined,
      roster: selectedRoster._id,
      ...(selectedRoster.isOnCall
        ? {
            assignedSubcontractor: null,
            assignToSubcontractor: false,
            assignedToSubcontractor: false,
            staffCanBook: false,
            ...(selectedShift.assignedSubcontractor ||
            selectedShift.assignedToSubcontractor
              ? { assignedTo: null }
              : {}),
          }
        : {}),
    })
    setDrawerOpen(true)
  }

  useEffect(() => {
    if (!requestedShiftID || loading) return

    const requestKey = `${requestedShiftID}:${requestedDate ?? ''}`
    if (openedRequestedShiftKey.current === requestKey) return

    const requestedShift = shifts.find(
      (shift) => shift._id === requestedShiftID,
    )
    if (!requestedShift) return

    openedRequestedShiftKey.current = requestKey
    setFilter('all')
    setDisplayMode('staff')
    openExistingShift(requestedShift)
  }, [requestedShiftID, requestedDate, shifts, loading])

  const handleRoleChange = (role) => {
    setShiftForm((prev) => ({
      ...prev,
      clinicalRole: role,
      payRate:
        prev.payRate === '' || prev.payRate === undefined
          ? (orgSettings?.defaultPayRates?.[role] ?? '')
          : prev.payRate,
    }))
  }

  const saveRoster = () => {
    setRosterSaving(true)
    API.post('/rosters', {
      ...rosterForm,
      baseLocation: rosterForm.baseLocation?._id ?? null,
    })
      .then((res) => {
        if (res.status === 200) {
          setRosters((prev) => [...prev, res.data])
          setSelectedRoster(res.data)
          setRosterDialogOpen(false)
          setToast({ message: 'Rota plan created' })
        }
      })
      .finally(() => setRosterSaving(false))
  }

  const openPublishRoster = () => {
    if (!selectedRoster?._id) return

    setPublishSummary(null)
    setPublishRosterOpen(true)
    API.get(`/rosters/${selectedRoster._id}/publish-summary`).then((res) => {
      if (res.status === 200) setPublishSummary(res.data)
    })
  }

  const publishRoster = () => {
    if (!selectedRoster?._id) return

    setPublishingRoster(true)
    API.post(`/rosters/${selectedRoster._id}/publish`)
      .then((res) => {
        if (res.status === 200) {
          setPublishedRosterInfo(selectedRoster)
          setSelectedRoster(null)
          setRosters((prev) => prev.filter((r) => r._id !== res.data._id))
          setPublishRosterOpen(false)
          setPublishSummary(null)
          loadRosters()
          loadShifts()
        }
      })
      .finally(() => setPublishingRoster(false))
  }

  const archiveRoster = () => {
    if (!selectedRoster?._id) return

    setArchiveRosterError('')
    API.delete(`/rosters/${selectedRoster._id}`).then((res) => {
      if (res.status === 200) {
        setToast({ severity: 'info', message: 'Rota plan archived' })
        setArchiveRosterOpen(false)
        setSelectedRoster(null)
        loadRosters()
        loadShifts()
      } else {
        setArchiveRosterError(
          res.data?.error ||
            'Unable to archive this rota plan. Check whether it has verified or paid shifts.',
        )
      }
    })
  }

  const getAssignedEmployeeID = () => {
    if (
      shiftForm.assignedToSubcontractor ||
      shiftForm.assignedTo?.subcontractor
    ) {
      return null
    }

    return getSelectedEmployeeID(shiftForm.assignedTo)
  }

  const getAssignedSubcontractorID = () => {
    if (shiftForm.assignedToSubcontractor) return null

    return (
      getSelectedSubcontractorID(shiftForm.assignedTo) ??
      shiftForm.assignedSubcontractor?.subcontractor?._id ??
      null
    )
  }

  const saveShift = () => {
    setShiftSaving(true)
    API.post('/roster-shift', {
      ...shiftForm,
      start: shiftForm.start.toISOString(),
      end: shiftForm.end.toISOString(),
      repeat: {
        ...shiftForm.repeat,
        until: shiftForm.repeat?.until?.toISOString?.() ?? null,
      },
      assignedTo: getAssignedEmployeeID(),
      assignedSubcontractor: getAssignedSubcontractorID(),
      assignToSubcontractor:
        !!shiftForm.assignedToSubcontractor || !!getAssignedSubcontractorID(),
      assignedToSubcontractor: !!shiftForm.assignedToSubcontractor,
      payRate: shiftForm.payRate === '' ? undefined : Number(shiftForm.payRate),
      travel: shiftForm.travel ?? null,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: `Shift ${shiftForm.existingShift ? 'updated' : 'created'}`,
          })
          setDrawerOpen(false)
          loadShifts()
          loadShiftSwapRequests()
          return
        }
        // The API client resolves on 4xx/5xx (validateStatus), so a rejected
        // save has to be read here - it used to fall through silently and the
        // drawer just sat there with nothing saved
        if (
          res.status === 400 &&
          res.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setAssignmentIssues(res.data.issues ?? [])
          setToast({
            severity: 'error',
            message: 'Shift assignment has scheduling issues',
          })
          return
        }
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to save this shift',
        })
      })
      .catch(() => {
        setToast({ severity: 'error', message: 'Unable to save this shift' })
      })
      .finally(() => setShiftSaving(false))
  }

  const saveSourceShiftEdit = () => {
    if (!selectedShift?._id) return

    setShiftSaving(true)
    API.post('/global-roster/shift-edit', {
      shiftID: selectedShift._id,
      start: shiftForm.start.toISOString(),
      end: shiftForm.end.toISOString(),
      clinicalRole: shiftForm.clinicalRole,
      shiftNote: shiftForm.shiftNote,
      payRate: shiftForm.payRate === '' ? undefined : Number(shiftForm.payRate),
      hourlyRate: shiftForm.hourlyRate,
      bonuses: (shiftForm.bonuses ?? []).filter(
        (bonus) => bonus.amount !== '' && Number(bonus.amount) >= 0,
      ),
      // null clears paid travel; the server normalises the object form
      travel: shiftForm.travel ?? null,
      staffCanBook: shiftForm.staffCanBook,
      assignedTo: getAssignedEmployeeID(),
      assignedSubcontractor: getAssignedSubcontractorID(),
      assignToSubcontractor:
        !!shiftForm.assignedToSubcontractor || !!getAssignedSubcontractorID(),
      assignedToSubcontractor: !!shiftForm.assignedToSubcontractor,
      applyToIdenticalVacantShifts: (selectedShift?.duplicateCount ?? 1) > 1,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message:
              (selectedShift?.duplicateCount ?? 1) > 1
                ? `${selectedShift.duplicateCount} shifts updated`
                : 'Shift updated',
          })
          setDrawerOpen(false)
          loadShifts()
          loadApplications()
          loadShiftSwapRequests()
          return
        }
        // Same as saveShift: non-200s resolve rather than reject
        if (
          res.status === 400 &&
          res.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setAssignmentIssues(res.data.issues ?? [])
          setToast({
            severity: 'error',
            message: 'Shift assignment has scheduling issues',
          })
          return
        }
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to update this shift',
        })
      })
      .catch(() => {
        setToast({ severity: 'error', message: 'Unable to update this shift' })
      })
      .finally(() => setShiftSaving(false))
  }

  // Accepts a "Cannot Attend": the person comes off the shift and it's left
  // open (and bookable) on Available Shifts
  const releaseShift = () => {
    if (!selectedShift?._id) return

    setReleasingShift(true)
    API.post(`/global-roster/shifts/${selectedShift._id}/release`)
      .then((res) => {
        if (res.status === 200) {
          const pending = res.data?.pendingApplications ?? 0
          setToast({
            message: res.data?.advertised
              ? `Released - shift is now open on Available Shifts${
                  pending
                    ? ` (${pending} application${pending === 1 ? '' : 's'} pending)`
                    : ''
                }`
              : 'Released - shift is now vacant',
          })
          setDrawerOpen(false)
          loadShifts()
          loadApplications()
          loadShiftSwapRequests()
          return
        }
        // Non-200s resolve rather than reject (validateStatus)
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to release this shift',
        })
      })
      .catch(() => {
        setToast({ severity: 'error', message: 'Unable to release this shift' })
      })
      .finally(() => setReleasingShift(false))
  }

  const deleteShift = () => {
    if (!selectedShift?._id) return

    API.delete(`/roster-shift/${selectedShift._id}`)
      .then((res) => {
        if (res.status === 200) {
          setToast({ severity: 'info', message: 'Shift deleted' })
          setDrawerOpen(false)
          loadShifts()
        }
      })
      .catch((e) => {
        setToast({
          severity: 'error',
          message: e?.response?.data?.error || 'Unable to delete this shift',
        })
      })
  }

  const selectedShiftIsRosterShift = !!selectedShift?.roster || !selectedShift
  const selectedShiftLocked = selectedShift?.isPaid || selectedShift?.isVerified
  const hasBlockingAssignmentIssues = assignmentIssues.some(
    (assignmentIssue) => assignmentIssue.severity !== 'warning',
  )
  const applicationsInView = useMemo(
    () =>
      applications.filter((application) =>
        moment(application?.shift?.start).isBetween(
          startDate,
          endDate.clone().endOf('day'),
          'minute',
          '[]',
        ),
      ),
    [applications, startDate.valueOf(), endDate.valueOf()],
  )
  // Open, staff-bookable Event vacancies are advertised as one merged
  // listing, so an applicant's application may be bound to any identical
  // sibling Shift document rather than this exact one - match by identity
  // (event/role/time/pay) instead of exact shift id so applications and the
  // "has applicants" indicator surface against whichever vacant slot the
  // manager happens to be looking at. Indexed once per load: the year view
  // asks for every shift's applications, so a per-call scan would be
  // shifts × applications identity-key computations.
  const applicationsByIdentity = useMemo(() => {
    const index = new Map()
    applicationsInView.forEach((application) => {
      const key = getShiftIdentityKey(application?.shift)
      if (!index.has(key)) index.set(key, [])
      index.get(key).push(application)
    })
    return index
  }, [applicationsInView])
  const getShiftApplications = useMemo(
    () => (shift) =>
      applicationsByIdentity.get(getShiftIdentityKey(shift)) ?? [],
    [applicationsByIdentity],
  )
  const selectedShiftApplications =
    selectedShift &&
    !selectedShift.assignedTo &&
    !selectedShift.assignedSubcontractor &&
    !selectedShift.assignedToSubcontractor
      ? getShiftApplications(selectedShift)
      : []

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Stack gap={2}>
        <PageHeader />

        <RosterStatusStrip roster={selectedRoster} />

        <RosterControlPanel
          rosters={rosters}
          selectedRoster={selectedRoster}
          setSelectedRoster={setSelectedRoster}
          openRosterDialog={openRosterDialog}
          openNewShift={openNewShift}
          publishRoster={openPublishRoster}
          openArchiveRoster={() => {
            setArchiveRosterError('')
            setArchiveRosterOpen(true)
          }}
          applicationsInView={applicationsInView}
          applicationCount={applications.length}
          onOpenApplications={() => navigate('/shift-applications')}
          shiftSwapRequestCount={shiftSwapRequests.length}
          onOpenShiftSwapRequests={() => navigate('/shift-swap-requests')}
          onCallNowNames={onCallNowNames}
          coverageItems={coverageItems}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          filter={filter}
          setFilter={setFilter}
          availabilityToggleVisible={
            viewRange === 'week' && displayMode === 'staff'
          }
          showAvailableOnly={showAvailableOnly}
          setShowAvailableOnly={setShowAvailableOnly}
          availableStaffCount={availableStaffCount}
          handlePrev={handlePrev}
          handleNext={handleNext}
          rangeLabel={rangeLabel}
          rangeLabelShort={rangeLabelShort}
          showNext12Months={
            viewRange === 'year' && !isRollingYear ? showNext12Months : null
          }
          viewRange={viewRange}
          setViewRange={setViewRange}
          defaultView={defaultView}
          saveDefaultView={saveDefaultView}
          savingDefaultView={savingDefaultView}
        />

        {loading && <LinearProgress />}

        {viewRange === 'week' && (
          <RosterGrid
            days={days}
            groupedRows={groupedRows}
            selectedRoster={selectedRoster}
            startDate={startDate}
            filter={filter}
            openNewShift={openNewShift}
            openExistingShift={openExistingShift}
            getShiftApplications={getShiftApplications}
            showAvailableOnly={displayMode === 'staff' && showAvailableOnly}
            displayMode={displayMode}
            availableStaffByDay={availableStaffByDay}
            uncrewedDuties={visibleUncrewedDuties}
          />
        )}
        {viewRange === 'month' && (
          <RosterMonthCalendar
            monthStart={startDate}
            shifts={filteredShifts}
            displayMode={displayMode}
            selectedRoster={selectedRoster}
            getShiftApplications={getShiftApplications}
            openExistingShift={openExistingShift}
            openNewShift={openNewShift}
            onOpenWeek={openWeekOf}
            availableStaffByDay={availableStaffByDay}
          />
        )}
        {viewRange === 'year' && (
          <RosterYearOverview
            yearStart={startDate}
            yearEnd={endDate}
            shifts={filteredShifts}
            selectedRoster={selectedRoster}
            getShiftApplications={getShiftApplications}
            onSelectDay={openWeekOf}
            displayMode={displayMode}
            availableStaffByDay={availableStaffByDay}
          />
        )}
      </Stack>

      <NewRosterDialog
        open={rosterDialogOpen}
        onClose={() => {
          setRosterDialogOpen(false)
          setRosterForm(rosterFormDefault)
        }}
        rosterForm={rosterForm}
        setRosterForm={setRosterForm}
        baseLocationOptions={baseLocationOptions}
        rosterSaving={rosterSaving}
        saveRoster={saveRoster}
      />

      <ArchiveRosterDialog
        open={archiveRosterOpen}
        onClose={() => setArchiveRosterOpen(false)}
        selectedRoster={selectedRoster}
        archiveRosterError={archiveRosterError}
        archiveRoster={archiveRoster}
      />

      <PublishRosterDialog
        open={publishRosterOpen}
        onClose={() => {
          setPublishRosterOpen(false)
          setPublishSummary(null)
        }}
        selectedRoster={selectedRoster}
        publishSummary={publishSummary}
        publishRoster={publishRoster}
        publishing={publishingRoster}
      />

      <RosterPublishedDialog
        open={!!publishedRosterInfo}
        onClose={() => setPublishedRosterInfo(null)}
        publishedRoster={publishedRosterInfo}
      />

      <ShiftDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedShift={selectedShift}
        selectedShiftIsRosterShift={selectedShiftIsRosterShift}
        selectedShiftLocked={selectedShiftLocked}
        selectedShiftApplications={selectedShiftApplications}
        setSelectedApplication={setSelectedApplication}
        shiftForm={shiftForm}
        setShiftForm={setShiftForm}
        shiftIsOnCall={shiftFormIsOnCall}
        rosters={rosters}
        selectedRoster={selectedRoster}
        copyShiftToSelectedRoster={copyShiftToSelectedRoster}
        handleRoleChange={handleRoleChange}
        checkingAssignment={checkingAssignment}
        assignmentIssues={assignmentIssues}
        orgSettings={orgSettings}
        hasBlockingAssignmentIssues={hasBlockingAssignmentIssues}
        shiftSaving={shiftSaving}
        deleteShift={deleteShift}
        releaseShift={releaseShift}
        releasingShift={releasingShift}
        saveShift={saveShift}
        saveSourceShiftEdit={saveSourceShiftEdit}
      />

      <Drawer
        anchor="right"
        open={!!selectedApplication}
        onClose={() => setSelectedApplication(null)}
      >
        {selectedApplication && (
          <SelectedApplication
            application={selectedApplication}
            onClose={() => setSelectedApplication(null)}
            loadApplications={() => {
              loadApplications()
              loadShifts()
            }}
          />
        )}
      </Drawer>
    </LocalizationProvider>
  )
}

export default GlobalRosterCalendar
